import { motion } from "framer-motion";
import { CountUp } from "@/components/site/CountUp";
import { TRACK_RECORD } from "@/lib/track-record";
import "./FinalSections.css";

const featured = {
  ...TRACK_RECORD[0],
  body: "140% revenue growth.",
  method: "Built buyer targeting and outreach from scratch, then trained the sales team to use them.",
};

const studies = [
  {
    ...TRACK_RECORD[1],
    body: (
      <>
        <span className="block">
          Meetings went from <span className="font-semibold text-ink">6 to 20 a month.</span>
        </span>
      </>
    ),
    method: "Rebuilt targeting, messaging and follow-up to book more qualified meetings.",
    accent: "#FF4400",
  },
  {
    ...TRACK_RECORD[2],
    body: (
      <>
        <span className="block">Booked directors at Balfour Beatty, BAM and Bouygues.</span>
      </>
    ),
    method: "Targeted major contractors and reached their directors to open enterprise deals.",
    accent: "#0E8BD6",
  },
];

export const CaseStudies = () => {
  return (
    <section id="results" className="bg-paper border-t border-ink" data-testid="case-studies">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
        <h2 className="font-display font-semibold tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-ink max-w-4xl text-balance">
          Closed. Not <span className="font-editorial italic font-medium text-cobalt">projected.</span>
        </h2>
        <p className="mt-4 text-lg md:text-xl font-light text-ink leading-snug max-w-2xl text-pretty">
          Sales results from campaigns Hamza built and ran.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="case-card case-featured group relative mt-8 overflow-hidden bg-ink text-paper border border-ink p-7 md:p-10"
          data-testid="case-unity"
        >
          <div className="absolute top-0 left-0 h-2 w-full overflow-hidden" aria-hidden>
            <motion.div
              className="h-full w-full bg-cobalt origin-left"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="case-heading-row">
            <span className="relative font-display font-semibold text-2xl md:text-3xl tracking-tight">{featured.wordmark}</span>
          </div>
          <div className="relative mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <CountUp
                value={featured.metric}
                className="case-revenue block font-display font-semibold text-7xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.82] tabular-nums text-cobalt stat-on-ink"
              />
              <span className="mt-3 block text-lg md:text-xl text-paper">{featured.metricLabel}</span>
            </div>
            <div className="max-w-lg md:pb-2">
              <p className="text-xl md:text-2xl font-light text-white leading-snug text-pretty">{featured.body}</p>
              <div className="case-method mt-6 border-t border-white/20 pt-5">
                <span className="block font-mono text-sm md:text-base font-semibold uppercase tracking-[0.14em] text-flame">How</span>
                <p className="mt-2 text-lg md:text-xl font-light text-white leading-snug text-pretty">{featured.method}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-4 md:mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {studies.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              style={{ "--accent": s.accent }}
              className="case-card group relative bg-surface-light border border-ink p-7 md:p-9 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-hard"
              data-testid={`case-${s.wordmark.toLowerCase()}`}
            >
              <div className="absolute top-0 left-0 h-2 w-full [background-color:var(--accent)]" aria-hidden />
              <div className="case-heading-row">
                <span className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-ink">{s.wordmark}</span>
              </div>
              <CountUp
                value={s.metric}
                className="case-revenue mt-8 font-display font-semibold text-6xl md:text-7xl tracking-tighter leading-[0.85] tabular-nums"
                style={{ color: s.accent }}
              />
              <span className="mt-3 text-lg md:text-xl text-ink">{s.metricLabel}</span>
              <p className="mt-6 text-xl md:text-2xl font-light text-ink leading-snug border-t border-ink/15 pt-6 text-pretty">{s.body}</p>
              <div className="case-method mt-6 border-t border-ink/15 pt-5">
                <span className="block font-mono text-sm md:text-base font-semibold uppercase tracking-[0.14em] text-flame">How</span>
                <p className="mt-2 text-lg md:text-xl font-light text-ink leading-snug text-pretty">{s.method}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
